import { useState } from 'react';

import { AlertCircleIcon, ArrowDown01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { Collapse } from 'react-bootstrap';

import { Table } from 'components/Table/Table';

const SEVERITY_HIGH = 'high';

export const ValidationIssuesReview = ({ validationIssues = [], entityLabel = 'candidates' }) => {
  const singularLabel = entityLabel.endsWith('s') ? entityLabel.slice(0, -1) : entityLabel;
  const [issuesOpen, setIssuesOpen] = useState(true);

  const issues = Array.isArray(validationIssues) ? validationIssues.filter(Boolean) : [];
  const blockedIssues = issues.filter(issue => issue.severity === SEVERITY_HIGH);
  const warningIssues = issues.filter(issue => issue.severity !== SEVERITY_HIGH);

  const blockedRowCount = new Set(blockedIssues.map(issue => issue.csv_index)).size;
  const warningRowCount = new Set(warningIssues.map(issue => issue.csv_index)).size;

  if (issues.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-8 w-full text-text-primary">
      <div className="bg-white rounded-2xl border border-border-primary shadow-sm shadow-soft overflow-hidden">
        <Table.Context onQueryUpdate={() => {}} manualQueryUpdate defaultQuery={{}}>
          <Table>
            <Table.Head className="!h-auto min-h-0">
              <Table.Row>
                <Table.Header className="w-[38%] !py-4 !px-8 text-text-secondary font-light text-lg bg-surface-secondary border-b border-border-primary">
                  Status
                </Table.Header>
                <Table.Header className="w-[42%] !py-4 !px-8 text-text-secondary font-light text-lg bg-surface-secondary border-b border-border-primary">
                  Description
                </Table.Header>
                <Table.Header
                  align="right"
                  className="w-[20%] !py-4 !px-8 text-text-secondary font-light text-lg bg-surface-secondary border-b border-border-primary">
                  Count
                </Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {blockedRowCount > 0 && (
                <Table.Row>
                  <Table.Col className="w-[38%] !p-8 align-middle border-b border-border-primary">
                    <span className="inline-flex items-center gap-4 text-xl font-medium text-text-primary">
                      <HugeiconsIcon
                        icon={AlertCircleIcon}
                        className="text-error shrink-0"
                        size={22}
                        strokeWidth={1.5}
                      />
                      <span>Validation error</span>
                    </span>
                  </Table.Col>
                  <Table.Col className="w-[42%] !p-8 align-middle border-b border-border-primary text-text-tertiary font-light text-xl leading-normal">
                    Missing or invalid required fields — will be skipped
                  </Table.Col>
                  <Table.Col
                    align="right"
                    className="w-[20%] !p-8 text-3xl font-medium text-text-primary align-middle border-b border-border-primary">
                    {blockedRowCount}
                  </Table.Col>
                </Table.Row>
              )}
              {warningRowCount > 0 && (
                <Table.Row>
                  <Table.Col className="w-[38%] !p-8 align-middle border-b border-border-primary">
                    <span className="inline-flex items-center gap-4 text-xl font-medium text-text-primary">
                      <HugeiconsIcon
                        icon={AlertCircleIcon}
                        className="text-text-status-pending shrink-0"
                        size={22}
                        strokeWidth={1.5}
                      />
                      <span>Warnings</span>
                    </span>
                  </Table.Col>
                  <Table.Col className="w-[42%] !p-8 align-middle border-b border-border-primary text-text-tertiary font-light text-xl leading-normal">
                    Unusual data — will still be imported
                  </Table.Col>
                  <Table.Col
                    align="right"
                    className="w-[20%] !p-8 text-3xl font-medium text-text-primary align-middle border-b border-border-primary">
                    {warningRowCount}
                  </Table.Col>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </Table.Context>
      </div>

      <div className="bg-white border border-border-primary rounded-2xl overflow-hidden shadow-sm shadow-soft">
        <div className="flex items-center justify-between gap-4 w-full px-8 py-6 bg-white text-text-primary transition-colors duration-200 hover:bg-surface-secondary">
          <div
            role="button"
            tabIndex={0}
            aria-expanded={issuesOpen}
            className="flex flex-1 min-w-0 items-center gap-0 m-0 border-0 bg-transparent text-text-primary font-inherit text-left cursor-pointer select-none rounded-xl -my-1 -mx-1 px-2 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-text-primary"
            onClick={() => setIssuesOpen(o => !o)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIssuesOpen(o => !o);
              }
            }}>
            <span className="flex flex-col items-start gap-0.5 min-w-0 flex-1">
              <span className="primary-text u-font-size-small u-font-weight-500">
                {issues.length} data issue{issues.length === 1 ? '' : 's'} found
              </span>
              <span className="secondary-text u-font-size-x-small">
                {blockedRowCount > 0
                  ? `${blockedRowCount} ${blockedRowCount === 1 ? singularLabel : entityLabel} will be skipped`
                  : `All ${entityLabel} will still be imported`}
              </span>
            </span>
          </div>
          <button
            type="button"
            aria-expanded={issuesOpen}
            aria-label={issuesOpen ? 'Collapse validation issues' : 'Expand validation issues'}
            className="flex items-center justify-center w-10 h-10 p-0 border-0 rounded-full bg-transparent text-text-tertiary cursor-pointer transition-colors duration-200 hover:text-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-text-primary"
            onClick={() => setIssuesOpen(o => !o)}>
            <HugeiconsIcon
              icon={ArrowDown01Icon}
              className={cn('transition-transform duration-200', { 'rotate-180': issuesOpen })}
              size={24}
              strokeWidth={1.5}
            />
          </button>
        </div>
        <Collapse in={issuesOpen}>
          <div className="border-t border-border-primary">
            <ul className="list-none m-0 p-0 max-h-[400px] overflow-y-auto flex flex-col gap-0">
              {issues.map((issue, index) => {
                const isBlocked = issue.severity === SEVERITY_HIGH;
                const rowNum =
                  typeof issue.csv_index === 'number' && !Number.isNaN(issue.csv_index)
                    ? issue.csv_index
                    : index;
                return (
                  <li
                    key={`issue-${rowNum}-${index}`}
                    className="flex items-center justify-between gap-4 px-8 py-4 bg-white border-t border-border-primary first:border-t-0 text-text-primary">
                    <div className="flex flex-col gap-1 min-w-0">
                      <span className="text-text-secondary font-medium text-base uppercase tracking-wide leading-tight">
                        Row {rowNum + 1}
                      </span>
                      <span className="text-xl font-light text-text-primary break-words leading-normal">
                        {issue.message}
                      </span>
                    </div>
                    <span
                      className={cn(
                        'shrink-0 text-base font-medium px-3 py-1 rounded-full',
                        isBlocked ? 'text-error bg-red-1' : 'text-text-status-pending bg-yellow-1',
                      )}>
                      {isBlocked ? 'Skipped' : 'Warning'}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </Collapse>
      </div>
    </div>
  );
};

export default ValidationIssuesReview;
